import Editor from "@monaco-editor/react";
import DownloadIcon from "@mui/icons-material/Download";
import SaveIcon from "@mui/icons-material/Save";
import { Box, Button, CircularProgress, Stack, Tooltip, Typography } from "@mui/material";
import { memo, useEffect, useRef, useState } from "react";
import alqemiDataService, { Candidate } from "../../api/AlqemiDataService";
import Dropdown from "../../common/components/fundamentals/Dropdown";
import CircularLoader from "../../common/components/fundamentals/Loader/CircularLoader";
import { usePopUp } from "../../common/context/PopUpProvider";
import { useSnackbar } from "../../common/context/SnackbarProvider";
import { useUser } from "../../common/context/UserProvider";
import { HeadingText, SmallText } from "../../common/styles/Text.styles";
import CVPDF from "../../cv-generation/CVPDF";
import {
  SettingsArea,
  SettingsButtonSection,
} from "../../pages/Settings/Settings.styles";
import {
  borderStyle,
  defaultGap,
  smallerGap,
  smallGap,
} from "../../styles/consts";
import { replaceWhitespaceForDash, hasSalesOrAdminAccess } from "../../utils";
import PopUpBox from "../components/PopUpBox";

const MemoizedCVPDF = memo(
  ({
    cvContent,
    setPdfDownloadBlob,
  }: {
    cvContent: any;
    setPdfDownloadBlob: (blob: Blob | null) => void;
  }) => {
    return (
      <CVPDF cvContent={cvContent} setPdfDownloadBlob={setPdfDownloadBlob} />
    );
  },
  (prevProps, nextProps) => {
    return (
      JSON.stringify(prevProps.cvContent) ===
      JSON.stringify(nextProps.cvContent)
    );
  },
);

export default function ManageConsultantsCVs() {
  const { showSnackbar } = useSnackbar();
  const { showPopUpWindow } = usePopUp();
  const { user } = useUser();
  const [consultants, setConsultants] = useState<Candidate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedConsultantId, setSelectedConsultantId] = useState("");
  const [selectedCvId, setSelectedCvId] = useState("");
  const [selectedConsultant, setSelectedConsultant] =
    useState<Candidate | null>(null);
  const [pdfDownloadBlob, setPdfDownloadBlob] = useState<Blob | null>(null);
  const [readyForPdf, setReadyForPdf] = useState(false);
  const [editingCvContent, setEditingCvContent] = useState<any>(null);
  const [previewCvContent, setPreviewCvContent] = useState<any>(null);
  const [editingPdfBlob, setEditingPdfBlob] = useState<Blob | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [forceClosePopUp, setForceClosePopUp] = useState(false);
  const editorRef = useRef<any>(null);
  const editingCvContentRef = useRef<any>(null);

  useEffect(() => {
    const fetchConsultants = async () => {
      setIsLoading(true);
      try {
        const allConsultants = await alqemiDataService.getCandidates();
        let activeConsultants = allConsultants.filter((c) => !c.archived);

        if (!hasSalesOrAdminAccess(user)) {
          activeConsultants = activeConsultants.filter(
            (c) => c.supervisor_email === user.email,
          );
        }

        setConsultants(activeConsultants);
      } catch (error) {
        showSnackbar("Error loading consultants", true);
      }
      setIsLoading(false);
    };

    fetchConsultants();
  }, [user.sales, user.admin, user.email, showSnackbar]);

  const handleConsultantChange = async (consultantId: string) => {
    setSelectedConsultantId(consultantId);
    setSelectedCvId("");
    setEditingCvContent(null);
    setPreviewCvContent(null);
    setEditingPdfBlob(null);
    setHasUnsavedChanges(false);
    setPdfDownloadBlob(null);

    if (consultantId && consultantId !== "None") {
      try {
        const consultant = await alqemiDataService.getCandidate(consultantId);
        setSelectedConsultant(consultant);

        if (consultant.cvs && consultant.cvs.length > 0) {
          setSelectedCvId(consultant.cvs[0].id);
        }
      } catch (error) {
        showSnackbar("Error loading consultant data", true);
      }
    } else {
      setSelectedConsultant(null);
    }
  };

  const handleCvChange = (cvId: string) => {
    if (cvId === "None") {
      setSelectedCvId("");
    } else {
      setSelectedCvId(cvId);
    }
    setEditingCvContent(null);
    setPreviewCvContent(null);
    setEditingPdfBlob(null);
    setHasUnsavedChanges(false);
    setPdfDownloadBlob(null);
  };

  const selectedCv = selectedConsultant?.cvs?.find(
    (cv) => cv.id === selectedCvId,
  );

  useEffect(() => {
    if (selectedCv) {
      setReadyForPdf(false);
      const timer = setTimeout(() => setReadyForPdf(true), 0);
      return () => clearTimeout(timer);
    }
    setReadyForPdf(false);
  }, [selectedCv]);

  const handleEditorChange = (value: string | undefined) => {
    try {
      const parsedData = JSON.parse(value || "{}");
      setEditingCvContent(parsedData);
      editingCvContentRef.current = parsedData;
      setHasUnsavedChanges(true);
    } catch (error) {}
  };

  const handleRefreshPreview = () => {
    const content = editingCvContentRef.current;
    if (content) {
      setIsRefreshing(true);
      setEditingPdfBlob(null);
      setPreviewCvContent(content);
      setHasUnsavedChanges(false);
    }
  };

  const handleDownloadEditedCV = () => {
    if (editingPdfBlob && selectedConsultant && selectedCv) {
      const aTag = document.createElement("a");
      aTag.href = URL.createObjectURL(editingPdfBlob);
      aTag.style.display = "none";
      aTag.download = `Perelyn_Profile_${replaceWhitespaceForDash(
        selectedConsultant.name || "Unknown",
      )}_${selectedCv.name || "CV"}.pdf`;
      document.body.appendChild(aTag);
      aTag.click();
      document.body.removeChild(aTag);
    }
  };

  const handleBeforePopupClose = (): boolean => {
    const hasEdits =
      editingCvContent !== null &&
      selectedCv !== undefined &&
      JSON.stringify(editingCvContent) !== JSON.stringify(selectedCv?.content);
    if (hasEdits) {
      showPopUpWindow(
        () => setForceClosePopUp(true),
        "Unsaved Changes",
        "Your edits will be lost if you leave. Are you sure?",
        "Cancel",
        "Leave",
      );
      return false;
    }
    return true;
  };

  const handlePopupClose = () => {
    setEditingCvContent(null);
    editingCvContentRef.current = null;
    setPreviewCvContent(null);
    setEditingPdfBlob(null);
    setHasUnsavedChanges(false);
    setIsRefreshing(false);
    editorRef.current = null;
  };

  const handleDownloadCV = () => {
    if (pdfDownloadBlob && selectedConsultant && selectedCv) {
      const aTag = document.createElement("a");
      aTag.href = URL.createObjectURL(pdfDownloadBlob);
      aTag.style.display = "none";
      aTag.download = `Perelyn_Profile_${replaceWhitespaceForDash(
        selectedConsultant.name || "Unknown",
      )}_${selectedCv.name || "CV"}.pdf`;
      document.body.appendChild(aTag);
      aTag.click();
      document.body.removeChild(aTag);
    }
  };

  return (
    <SettingsArea>
      <Stack>
        <Stack sx={{ borderBottom: borderStyle }}>
          <Stack sx={{ padding: smallerGap }}>
            <HeadingText>Manage Consultants CVs</HeadingText>
            <SmallText>
              Select a consultant and their CV to view, edit, or download.
            </SmallText>
          </Stack>
        </Stack>

        <Stack sx={{ padding: `${smallGap} ${defaultGap}`, gap: defaultGap }}>
          {isLoading ? (
            <Stack
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "2rem",
              }}
            >
              <CircularLoader size={"2rem"} />
            </Stack>
          ) : (
            <Stack sx={{ gap: smallGap }}>
              <Stack>
                <SmallText sx={{ marginBottom: "0.5rem" }}>
                  Select Consultant
                </SmallText>
                <Dropdown
                  zIndexModifier={100}
                  options={[
                    { value: "None", name: "None" },
                    ...consultants.map((consultant) => ({
                      value: consultant.consultant_id || consultant.id,
                      name: `${consultant.name}`,
                    })),
                  ]}
                  initialSelected={selectedConsultantId || "None"}
                  onChange={handleConsultantChange}
                  width="100%"
                />
              </Stack>

              {selectedConsultant && (
                <Stack>
                  <SmallText sx={{ marginBottom: "0.5rem" }}>
                    Select CV
                  </SmallText>
                  <Dropdown
                    options={[
                      { value: "None", name: "None" },
                      ...(selectedConsultant.cvs?.map((cv) => ({
                        value: cv.id,
                        name: cv.name,
                      })) || []),
                    ]}
                    initialSelected={selectedCvId || "None"}
                    onChange={handleCvChange}
                    width="100%"
                  />
                </Stack>
              )}

              {selectedCv && (
                <SmallText sx={{ color: "#666" }}>
                  Last updated:{" "}
                  {new Date(selectedCv.updated_at).toLocaleDateString()}
                </SmallText>
              )}

              {readyForPdf && selectedCv && (
                <div style={{ display: "none" }}>
                  <CVPDF
                    cvContent={selectedCv.content}
                    setPdfDownloadBlob={setPdfDownloadBlob}
                  />
                </div>
              )}
            </Stack>
          )}
        </Stack>
      </Stack>

      <SettingsButtonSection>
        <PopUpBox
          button={
            <Button
              variant="outlined"
              disabled={!selectedCv}
              sx={{
                "&&": {
                  borderBottom: "none",
                  borderTop: "none",
                  borderRight: "none",
                },
              }}
            >
              View & Edit
            </Button>
          }
          heading={selectedCv ? `Edit CV: ${selectedCv.name}` : "Edit CV"}
          width="80%"
          height="90%"
          onBeforeClose={handleBeforePopupClose}
          onClose={handlePopupClose}
          closePopUp={forceClosePopUp}
          setClosePopUp={setForceClosePopUp}
        >
          <Stack
            sx={{
              height: "100%",
              width: "100%",
              maxWidth: "100%",
              overflow: "auto",
              border: borderStyle,
              display: "flex",
              flexDirection: "row",
            }}
          >
            {selectedCv && (
              <>
                <Box
                  sx={{
                    minWidth: "50%",
                    height: "100%",
                    overflow: "auto",
                    backgroundColor: "rgb(246, 246, 246)",
                    position: "relative",
                  }}
                >
                  <Editor
                    height="100%"
                    defaultLanguage="json"
                    defaultValue={JSON.stringify(selectedCv.content, null, 2)}
                    onChange={handleEditorChange}
                    onMount={(editor, monaco) => {
                      editorRef.current = editor;
                      setEditingCvContent(selectedCv.content);
                      editingCvContentRef.current = selectedCv.content;
                      setHasUnsavedChanges(false);

                      editor.addAction({
                        id: "refresh-preview",
                        label: "Refresh Preview",
                        keybindings: [
                          monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS,
                        ],
                        run: () => {
                          handleRefreshPreview();
                        },
                      });

                      editor.setValue(
                        JSON.stringify(selectedCv.content, null, 2),
                      );
                    }}
                    theme="vs-light"
                    options={{
                      minimap: { enabled: false },
                      fontSize: 14,
                      wordWrap: "on",
                      automaticLayout: true,
                      scrollBeyondLastLine: false,
                    }}
                  />
                  <Tooltip
                    title={hasUnsavedChanges ? "Save changes first to download the latest version" : ""}
                    arrow
                  >
                    <span style={{ position: "absolute", bottom: 16, left: 16, zIndex: 10 }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleDownloadEditedCV}
                        disabled={!editingPdfBlob || hasUnsavedChanges}
                        startIcon={<DownloadIcon />}
                        sx={{
                          backgroundColor: "#1976d2",
                          "&:hover": {
                            backgroundColor: "#1565c0",
                          },
                          boxShadow: 2,
                          textTransform: "none",
                        }}
                      >
                        Download
                      </Button>
                    </span>
                  </Tooltip>
                  {hasUnsavedChanges && (
                    <>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleRefreshPreview}
                        disabled={isRefreshing}
                        startIcon={
                          isRefreshing ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <SaveIcon />
                          )
                        }
                        sx={{
                          position: "absolute",
                          bottom: 16,
                          right: 16,
                          zIndex: 10,
                          backgroundColor: "#ff5950",
                          "&:hover": {
                            backgroundColor: "#e54842",
                          },
                          boxShadow: 2,
                          textTransform: "none",
                        }}
                      >
                        {isRefreshing ? "Refreshing..." : "Refresh Preview"}
                      </Button>
                      <Typography
                        variant="caption"
                        sx={{
                          position: "absolute",
                          bottom: 52,
                          right: 16,
                          zIndex: 10,
                          backgroundColor: "rgba(255, 255, 255, 0.9)",
                          padding: "4px 19px",
                          color: "#666",
                        }}
                      >
                        {navigator.platform.includes("Mac")
                          ? "Cmd+S"
                          : "Ctrl+S"}{" "}
                      </Typography>
                    </>
                  )}
                  {isRefreshing && !hasUnsavedChanges && (
                    <Button
                      variant="contained"
                      size="small"
                      disabled
                      startIcon={<CircularProgress size={16} color="inherit" />}
                      sx={{
                        position: "absolute",
                        bottom: 16,
                        right: 16,
                        zIndex: 10,
                        backgroundColor: "#ff5950",
                        boxShadow: 2,
                        textTransform: "none",
                      }}
                    >
                      Refreshing...
                    </Button>
                  )}
                </Box>
                <MemoizedCVPDF
                  cvContent={previewCvContent || selectedCv.content}
                  setPdfDownloadBlob={(blob) => {
                    setEditingPdfBlob(blob);
                    if (blob) setIsRefreshing(false);
                  }}
                />
              </>
            )}
          </Stack>
        </PopUpBox>

        <Button
          variant="primary"
          onClick={handleDownloadCV}
          disabled={!pdfDownloadBlob || !selectedCv}
          sx={{
            "&&": {
              border: "none",
              borderLeft: borderStyle,
              borderRadius: 0,
              "&:disabled": {
                border: "none",
                borderLeft: borderStyle,
              },
            },
          }}
        >
          Download CV
        </Button>
      </SettingsButtonSection>
    </SettingsArea>
  );
}
